// Cross-evidence: edges where a pedigree family-level finding (a syndrome)
// corroborates a Phenomizer Profile-Match disease/gene. Pure matcher — the
// caller supplies the already-computed client-side Profile Match.
//
// This is the FAMILY-PATTERN signal, deliberately distinct from Phenomizer
// Phase A's gene-holding "gene + phenotype" convergence chip (plan §12.2) —
// they target different evidence and must not double up.

import { CONFIDENCE } from '../constants';

export const CROSS_EVIDENCE_LABEL = Object.freeze({
  familyAndVariant: 'family + variant',
  familyOnly: 'family history'
});

// The chip names the evidence the syndrome actually rests on. Every edge used
// to say 'family + variant', so an HBOC or Lynch match resting on affected
// relatives alone — including one whose only genotype is a negative result —
// told the clinician a variant had been found in the family. 'variant' is in
// evidenceTypes only when the syndromes detector found a POSITIVE carrier.
//
// A phenotype-only syndrome (one affected person, no carrier) is family
// history only when that person is a RELATIVE. The syndromes detector counts
// the proband among the affected, so a proband who is the only affected person
// raises a moderate syndrome; a chip on their own Profile Match row would claim
// a family history the pedigree does not contain, and the Profile Match was
// built from that same diagnosis — circular. Such an edge is not emitted (null).
function chipLabelFor(syndrome, probandPid) {
  const evidence = syndrome.evidenceTypes || [];
  if (evidence.includes('variant'))
    return CROSS_EVIDENCE_LABEL.familyAndVariant;
  if (evidence.includes('family_pattern'))
    return CROSS_EVIDENCE_LABEL.familyOnly;
  const relatives = (syndrome.supportingIds || []).filter(
    (pid) => probandPid == null || String(pid) !== String(probandPid)
  );
  return relatives.length ? CROSS_EVIDENCE_LABEL.familyOnly : null;
}

export function detectCrossEvidence(analysisResult, profileMatch = {}) {
  const edges = [];
  const pmDiseaseIds = new Set((profileMatch.diseases || []).map((d) => d.id));
  const pmGeneSymbols = new Set(
    (profileMatch.genes || []).map((g) =>
      String(g.name || g.id || '').toUpperCase()
    )
  );
  const syndromes =
    (analysisResult.family && analysisResult.family.syndromes) || [];

  for (const s of syndromes) {
    if (s.ruledOut || s.confidence === CONFIDENCE.ruledOut) continue;
    const chipLabel = chipLabelFor(s, analysisResult.probandPid);
    if (!chipLabel) continue;
    for (const did of s.relatedDiseaseIds || []) {
      if (pmDiseaseIds.has(did)) {
        edges.push({
          source: { section: 'pedigree_match', resultId: s.id },
          target: {
            section: 'profile_match',
            resultType: 'disease',
            resultId: did
          },
          strength: s.confidence,
          chipLabel
        });
      }
    }
    for (const gene of s.relatedGenes || []) {
      if (pmGeneSymbols.has(String(gene).toUpperCase())) {
        edges.push({
          source: { section: 'pedigree_match', resultId: s.id },
          target: {
            section: 'profile_match',
            resultType: 'gene',
            resultId: gene
          },
          strength: s.confidence,
          chipLabel
        });
      }
    }
  }
  return edges;
}
